import { ClipboardCopyButton } from '@/components/ui/clipboard/clipboard-button';
import type { ButtonProps } from '@chakra-ui/react';
import type {
  DisplayRightsProposalSummary,
  MentionBoostAuthorizationStatus,
} from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { canCopyDisplayRightsLink } from './display-rights-status';

interface DisplayRightsCopyLinkButtonProps extends Omit<ButtonProps, 'value'> {
  proposal: DisplayRightsProposalSummary | null;
  /**
   * The authorization behind the proposal. Pass it wherever it is known: a link
   * on a withdrawn authorization buys terms Meta can never honour.
   */
  authorizationStatus?: MentionBoostAuthorizationStatus;
}

/**
 * "Copy the proposal link", wherever a sent proposal is shown.
 *
 * The link is half the product: a brand that delivers through its own channel
 * has nothing else to hand the creator. Leaving it visible only on the
 * post-send screen meant one closed drawer, one page reload or one queue failure
 * lost it — and the only way back was to REPLACE the offer, destroying it to
 * read its address.
 *
 * The URL comes from `summary.publicUrl`, produced by the same server helper as
 * the e-mail body and the send response. It is never rebuilt from `lookupKey`.
 */
export function DisplayRightsCopyLinkButton({
  proposal,
  authorizationStatus,
  size = 'xs',
  variant = 'outline',
  ...buttonProps
}: DisplayRightsCopyLinkButtonProps) {
  const { t } = useTranslation();

  // The gate lives HERE, once. Each surface used to decide for itself whether a
  // link was still worth offering, and they disagreed.
  if (!canCopyDisplayRightsLink(proposal, authorizationStatus)) {
    return null;
  }

  return (
    <ClipboardCopyButton
      value={proposal!.publicUrl}
      label={t('displayRights.actions.copyLink')}
      size={size}
      variant={variant}
      data-testid="display-rights-copy-link"
      {...buttonProps}
    />
  );
}
